import Swal from "sweetalert2";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import { AppConfig } from "../shared/utils/AppConfig.js";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { Fechas } from "../shared/utils/Fechas.js";
import { VacacionesService, ErrorNegocio } from "../shared/services/VacacionesService.js";
import { SesionService } from "../shared/services/SesionService.js";
import { Layout } from "../shared/components/navbar/Navbar.js";
import { Loading } from "../shared/components/Loading.js";
import { Ayuda } from "../shared/components/Ayuda.js";
import { FotoPerfil } from "../shared/components/FotoPerfil.js";
import { CodigoSolicitud } from "../shared/utils/CodigoSolicitud.js";
import listadoTemplate from "./templates/listado.html?raw";
import historialTemplate from "./templates/historial.html?raw";
import editarTemplate from "./templates/editar.html?raw";

const reglas = AppConfig.vacaciones;

// Textos de los iconos de ayuda (data-ayuda="clave" en los templates)
const AYUDAS = {
  dias: {
    titulo: "Tus días de vacaciones",
    html:
      "<p><b>Disponibles según RRHH:</b> los días que el sistema tiene registrados a tu favor. Solo bajan cuando RRHH liquida unas vacaciones.</p>" +
      "<p><b>Comprometidos:</b> días de pedidos que todavía no fueron liquidados: pendientes de autorización, autorizados o incluso ya tomados. Mientras no se liquiden, siguen contando.</p>" +
      "<p><b>Efectivos a tomar:</b> disponibles menos comprometidos. Es lo que realmente podés programar.</p>" +
      "<p class='mb-0'>Los días se calculan automáticamente y puede haber alguna demora en la sincronización. Ante cualquier duda, comunicate con RRHH.</p>",
  },
  fechas: {
    titulo: "Cómo elegir las fechas",
    html:
      "<p><b>Salida:</b> el primer día que te tomás. Tiene que ser al menos <b>" + reglas.anticipacionMinimaDias +
      " días</b> después de hoy, para que RRHH pueda verificar y liquidar.</p>" +
      "<p><b>Vuelta:</b> el último día de vacaciones. Al día siguiente retomás tus actividades.</p>" +
      "<p class='mb-0'>Máximo <b>" + reglas.maximoDiasCorridos + " días corridos</b> por pedido.</p>",
  },
};

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  timer: 2500,
  timerProgressBar: true,
  showConfirmButton: false,
});

export const VacacionesController = {
  // ---------------------------------------------------------------- listado

  async renderListado() {
    const emp = SesionService.empleado();
    Loading.mostrar();
    try {
      const [pedidos, resumen] = await Promise.all([VacacionesService.listar(), VacacionesService.resumen()]);

      const contenido = TemplateEngine.render(listadoTemplate, {
        nombre: emp.nombre,
        avatar: FotoPerfil.avatarHtml("avatar-lg"),
        diasEfectivos: resumen.diasEfectivos,
        diasComprometidos: resumen.diasComprometidos,
        diasDisponibles: resumen.diasDisponibles,
        fraseDias: VacacionesController.fraseDias(resumen.diasEfectivos),
        imagenTemporada: Fechas.imagenTemporada(),
        hayPedidos: pedidos.length > 0,
        pedidos: pedidos.map((p) => ({
          ...p,
          desdeTxt: Fechas.formatear(p.desde),
          hastaTxt: Fechas.formatear(p.hasta),
          claseEstado: VacacionesController.claseEstado(p.estado),
          tieneAcciones: p.editarHabilitado || p.borrarHabilitado,
        })),
      });

      Layout.render(contenido);
      Ayuda.bind(AYUDAS);
      document.querySelectorAll(".btn-borrar").forEach((btn) => {
        btn.addEventListener("click", () => VacacionesController.confirmarBorrar(btn.dataset));
      });
      document.querySelectorAll("[data-solapa]").forEach((btn) => {
        btn.addEventListener("click", () => VacacionesController.mostrarSolapa(btn.dataset.solapa));
      });
    } catch (error) {
      VacacionesController.manejarError(error, () => VacacionesController.renderListado());
    } finally {
      Loading.ocultar();
    }
  },

  // Solapas "Tus pedidos" / "Historial". El historial se pide a la API la primera vez que se abre.
  mostrarSolapa(nombre) {
    document.querySelectorAll("[data-solapa]").forEach((btn) => {
      const activa = btn.dataset.solapa === nombre;
      btn.classList.toggle("active", activa);
      btn.setAttribute("aria-selected", activa);
    });
    document.getElementById("solapaPedidos").hidden = nombre !== "pedidos";
    const panelHistorial = document.getElementById("solapaHistorial");
    panelHistorial.hidden = nombre !== "historial";
    if (nombre === "historial" && !panelHistorial.dataset.cargado) {
      VacacionesController.cargarHistorial();
    }
  },

  async cargarHistorial() {
    const panel = document.getElementById("solapaHistorial");
    panel.innerHTML = '<div class="text-center text-muted py-5"><div class="spinner-border text-primary" role="status"></div></div>';
    try {
      const pedidos = await VacacionesService.historial();
      panel.innerHTML = TemplateEngine.render(historialTemplate, {
        ok: true,
        hayPedidos: pedidos.length > 0,
        pedidos: pedidos.map((p) => ({
          ...p,
          desdeTxt: Fechas.formatear(p.desde),
          hastaTxt: Fechas.formatear(p.hasta),
          pedidaTxt: p.fPedida ? Fechas.formatear(p.fPedida) : "-",
          claseEstado: VacacionesController.claseEstado(p.estado),
        })),
      });
      panel.dataset.cargado = "1";
    } catch (error) {
      console.error("Error cargando historial:", error);
      panel.innerHTML = TemplateEngine.render(historialTemplate, { error: true });
      document.getElementById("btnReintentarHistorial")?.addEventListener("click", () => VacacionesController.cargarHistorial());
    }
  },

  // Acompania al numero grande (dias efectivos) en el hero
  fraseDias(dias) {
    if (dias <= 0) return "No te quedan días efectivos a tomar";
    if (dias === 1) return "Te queda un solo día efectivo a tomar";
    return "días efectivos a tomar";
  },

  claseEstado(estado) {
    if (estado.includes("Rechazada")) return "estado-rechazada";
    if (estado.startsWith("Autorizada")) return "estado-autorizada";
    return "estado-pendiente";
  },

  async confirmarBorrar({ id, desde, hasta }) {
    const { isConfirmed } = await Swal.fire({
      title: "¿Borrar este pedido?",
      html: `Las vacaciones del <b>${desde}</b> al <b>${hasta}</b> se van a eliminar.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, borrar",
      cancelButtonText: "No",
      confirmButtonColor: "#d64545",
      reverseButtons: true,
    });
    if (!isConfirmed) return;

    Loading.mostrar();
    try {
      await VacacionesService.borrar(Number(id));
      Toast.fire({ icon: "success", title: "Pedido borrado" });
      await VacacionesController.renderListado();
    } catch (error) {
      Loading.ocultar();
      if (error instanceof ErrorNegocio) {
        // p. ej. RRHH lo autorizo mientras tanto: se avisa y se refresca el listado
        await Swal.fire({ icon: "info", title: "No se pudo borrar", text: error.message });
        return VacacionesController.renderListado();
      }
      VacacionesController.manejarError(error, () => VacacionesController.renderListado());
    }
  },

  // ----------------------------------------------------------------- editar

  async renderEditar(params) {
    const id = Number.parseInt(params.get("id")) || 0;
    let pedido = null;

    if (id > 0) {
      Loading.mostrar();
      try {
        pedido = await VacacionesService.obtener(id);
      } catch (error) {
        Loading.ocultar();
        if (error instanceof ErrorNegocio) {
          await Swal.fire({ icon: "info", title: "Atención", text: error.message });
          window.location.hash = "vacaciones";
          return;
        }
        return Layout.renderError(() => VacacionesController.renderEditar(params));
      }
      Loading.ocultar();
    }

    const contenido = TemplateEngine.render(editarTemplate, {
      id,
      esAlta: id === 0,
      titulo: id > 0 ? "Modificar vacaciones" : "Nuevas vacaciones",
      // Al editar se conserva el cobro que ya tenia el pedido; en un alta va Adelanto.
      cobro: pedido?.cobro || reglas.cobroAdelanto,
      anticipacionMinima: reglas.anticipacionMinimaDias,
    });
    Layout.render(contenido);
    Ayuda.bind(AYUDAS);

    const form = VacacionesController.initFormulario(pedido);

    document.getElementById("btnLimpiarFechas").addEventListener("click", () => form.limpiar());
    document.getElementById("btnUrgente")?.addEventListener("click", () => form.setUrgente(true));
    document.getElementById("btnCancelarUrgente")?.addEventListener("click", () => form.setUrgente(false));
    document.getElementById("btnCopiarCodigo")?.addEventListener("click", () => form.copiarCodigo());
    document.getElementById("formEditar").addEventListener("submit", (e) => {
      e.preventDefault();
      VacacionesController.guardar(form);
    });
  },

  /**
   * Dos calendarios (salida y vuelta) con las reglas de negocio: la salida con
   * `anticipacionMinimaDias` de anticipacion y hasta `anticipacionMaximaDias` adelante; la vuelta
   * se habilita al elegir la salida y admite hasta `maximoDiasCorridos` corridos.
   * Al editar un pedido ya cargado se respetan sus fechas aunque hoy queden fuera del minimo.
   * En modo urgente la salida puede ser desde hoy y hace falta un codigo de habilitacion de RRHH.
   */
  initFormulario(pedido) {
    const hoy = new Date();
    const minNormal = Fechas.sumarDias(hoy, reglas.anticipacionMinimaDias);
    const maxSalida = Fechas.sumarDias(hoy, reglas.anticipacionMaximaDias);
    const minEdicion = pedido && new Date(pedido.desde) < minNormal ? new Date(pedido.desde) : minNormal;
    const opciones = { locale: Spanish, dateFormat: "d/m/Y", disableMobile: true };

    const form = { urgente: false, pickerDesde: null, pickerHasta: null };

    form.pickerHasta = flatpickr("#txtHasta", {
      ...opciones,
      minDate: pedido ? new Date(pedido.desde) : null,
      maxDate: pedido ? Fechas.sumarDias(new Date(pedido.desde), reglas.maximoDiasCorridos - 1) : null,
      defaultDate: pedido ? new Date(pedido.hasta) : null,
      onChange: () => form.actualizar(),
    });

    form.pickerDesde = flatpickr("#txtDesde", {
      ...opciones,
      minDate: minEdicion,
      maxDate: maxSalida,
      defaultDate: pedido ? new Date(pedido.desde) : null,
      onChange(selectedDates) {
        const desde = selectedDates[0];
        const inputHasta = document.getElementById("txtHasta");
        if (!desde) {
          form.pickerHasta.clear();
          inputHasta.disabled = true;
        } else {
          // La vuelta va entre la salida y N dias corridos despues; si la que estaba queda afuera, se limpia
          const maxHasta = Fechas.sumarDias(desde, reglas.maximoDiasCorridos - 1);
          form.pickerHasta.set("minDate", desde);
          form.pickerHasta.set("maxDate", maxHasta);
          const hasta = form.pickerHasta.selectedDates[0];
          if (hasta && (hasta < desde || hasta > maxHasta)) form.pickerHasta.clear();
          inputHasta.disabled = false;
          if (!form.pickerHasta.selectedDates[0]) form.pickerHasta.open();
        }
        form.actualizar();
      },
    });

    form.fechas = () => [form.pickerDesde.selectedDates[0], form.pickerHasta.selectedDates[0]];

    form.limpiar = () => {
      form.pickerDesde.clear();
      form.pickerHasta.clear();
      document.getElementById("txtHasta").disabled = true;
      form.actualizar();
    };

    form.setUrgente = (urgente) => {
      form.urgente = urgente;
      form.pickerDesde.set("minDate", urgente ? hoy : minEdicion);
      form.limpiar();
      document.getElementById("bloqueUrgente").hidden = !urgente;
      document.getElementById("btnUrgente").hidden = urgente;
      document.getElementById("txtHabilitacion").value = "";
      if (urgente) form.pickerDesde.open();
    };

    form.codigoSolicitud = () => {
      const desde = form.pickerDesde.selectedDates[0];
      if (!form.urgente || !desde) return null;
      try {
        return CodigoSolicitud.generar(SesionService.empleado()?.legajo, desde);
      } catch (error) {
        console.error("No se pudo armar el codigo de solicitud:", error);
        Toast.fire({ icon: "error", title: "No pudimos armar el código de solicitud. Salí y volvé a ingresar." });
        return null;
      }
    };

    form.copiarCodigo = async () => {
      const codigo = form.codigoSolicitud();
      if (!codigo) return;
      try {
        await navigator.clipboard.writeText(CodigoSolicitud.formatear(codigo));
        Toast.fire({ icon: "success", title: "Código copiado" });
      } catch {
        Toast.fire({ icon: "warning", title: "No se pudo copiar, anotalo a mano" });
      }
    };

    // Refresca resumen y, en modo urgente, el codigo de solicitud y sus botones
    form.actualizar = () => {
      VacacionesController.mostrarResumen(form.fechas());
      const codigo = form.codigoSolicitud();
      const el = document.getElementById("codigoSolicitud");
      const btnCopiar = document.getElementById("btnCopiarCodigo");
      const btnWa = document.getElementById("btnWhatsapp");
      if (!el) return;
      if (codigo) {
        const desde = form.pickerDesde.selectedDates[0];
        el.textContent = CodigoSolicitud.formatear(codigo);
        el.classList.remove("text-muted");
        btnCopiar.disabled = false;
        const texto = `Hola, necesito habilitación para vacaciones urgentes desde el ${Fechas.formatear(desde)}. Código de solicitud: ${CodigoSolicitud.formatear(codigo)}`;
        btnWa.href = "https://wa.me/?text=" + encodeURIComponent(texto);
        btnWa.classList.remove("disabled");
      } else {
        el.textContent = "elegí la fecha de salida";
        el.classList.add("text-muted");
        btnCopiar.disabled = true;
        btnWa.href = "#";
        btnWa.classList.add("disabled");
      }
    };

    form.actualizar();
    return form;
  },

  mostrarResumen([desde, hasta]) {
    const el = document.getElementById("resumenFechas");
    if (!el) return;
    if (!desde || !hasta) {
      el.hidden = true;
      return;
    }
    const dias = Fechas.diasCorridos(desde, hasta);
    el.innerHTML =
      `<i class="fas fa-sun me-1"></i><b>${dias} ${dias === 1 ? "día" : "días"} corridos</b>, ` +
      `del ${Fechas.formatearLargo(desde)} al ${Fechas.formatearLargo(hasta)}. ` +
      `Volvés a trabajar el ${Fechas.formatearLargo(Fechas.sumarDias(hasta, 1))}.`;
    el.hidden = false;
  },

  async guardar(form) {
    const id = Number.parseInt(document.getElementById("idVacacion").value) || 0;
    const cobro = Number.parseInt(document.getElementById("cobroVacacion").value) || reglas.cobroAdelanto;
    const [desde, hasta] = form.fechas();

    if (!desde || !hasta) {
      Toast.fire({ icon: "warning", title: "Elegí las fechas de salida y de vuelta" });
      return;
    }

    let codigoHabilitacion = null;
    if (form.urgente) {
      codigoHabilitacion = document.getElementById("txtHabilitacion").value.replace(/\s/g, "");
      if (!/^\d{6}$/.test(codigoHabilitacion)) {
        Toast.fire({ icon: "warning", title: "Ingresá el código de habilitación de 6 dígitos que te dio RRHH" });
        document.getElementById("txtHabilitacion").focus();
        return;
      }
    }

    const emp = SesionService.empleado();
    const dias = Fechas.diasCorridos(desde, hasta);
    if (dias > emp.diasDisponibles) {
      await Swal.fire({
        icon: "info",
        title: "No te alcanzan los días",
        text: `Ese pedido son ${dias} días y tenés ${emp.diasDisponibles} disponibles.`,
      });
      return;
    }

    const btn = document.getElementById("btnGuardar");
    btn.disabled = true;
    Loading.mostrar();
    try {
      await VacacionesService.guardar({ id, cobro, desde, hasta, codigoHabilitacion });
      Toast.fire({ icon: "success", title: id > 0 ? "Pedido modificado" : "Pedido cargado" });
      window.location.hash = "vacaciones";
    } catch (error) {
      if (error instanceof ErrorNegocio) {
        await Swal.fire({ icon: "info", title: "No se pudo guardar", text: error.message });
      } else {
        console.error("Error guardando vacaciones:", error);
        await Swal.fire({
          icon: "error",
          title: "Hubo un error",
          text: "No pudimos guardar el pedido. Intentá de nuevo en unos minutos; si sigue así, avisale a Sistemas.",
        });
      }
    } finally {
      btn.disabled = false;
      Loading.ocultar();
    }
  },

  // ----------------------------------------------------------------- comunes

  // ErrorNegocio aca significa "no encontramos tus datos / sesion expirada" (listar, obtener):
  // se avisa y se vuelve al acceso. El resto es error tecnico: pantalla con reintento.
  manejarError(error, onReintentar) {
    if (error instanceof ErrorNegocio) {
      console.warn(error.message);
      Swal.fire({ icon: "info", title: "Atención", text: error.message }).then(() => {
        SesionService.cerrar();
        window.location.hash = "acceso";
      });
      return;
    }
    console.error(error);
    Layout.renderError(onReintentar);
  },
};
